import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/** Copy text to the clipboard, with a short-lived "copied" flag for feedback. */
export function useCopy(resetAfterMs = 2000) {
  const [copied, setCopied] = useState(false)
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [])

  const copy = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        if (timeout.current) clearTimeout(timeout.current)
        timeout.current = setTimeout(() => setCopied(false), resetAfterMs)
        return true
      } catch {
        // Clipboard access is denied outside secure contexts and in some
        // embedded browsers. Report failure rather than pretending.
        return false
      }
    },
    [resetAfterMs]
  )

  return { copied, copy }
}

/** Debounce a fast-changing value — search inputs, filter boxes. */
export function useDebounce<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(id)
  }, [value, delayMs])

  return debounced
}

/** Type-safe localStorage-backed state that survives unavailable storage. */
export function useLocalStorage<T>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key)
      return raw === null ? initial : (JSON.parse(raw) as T)
    } catch {
      // Private browsing, disabled storage, or corrupt JSON.
      return initial
    }
  })

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value))
    } catch {
      // Non-fatal: the UI still works, it just will not persist.
    }
  }, [key, value])

  return [value, setValue] as const
}

/** Subscribe to a CSS media query. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches
  )

  useEffect(() => {
    const list = window.matchMedia(query)
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches)
    setMatches(list.matches)
    list.addEventListener('change', onChange)
    return () => list.removeEventListener('change', onChange)
  }, [query])

  return matches
}

/** True below the `md` breakpoint — drives the sidebar drawer. */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767px)')
}

/**
 * Call `handler` when a pointer event lands outside `ref`.
 * Used for dropdowns and menus that are not built on Radix.
 */
export function useClickOutside<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  handler: () => void,
  enabled = true
) {
  const saved = useRef(handler)
  saved.current = handler

  useEffect(() => {
    if (!enabled) return
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const el = ref.current
      if (!el || el.contains(event.target as Node)) return
      saved.current()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
    }
  }, [ref, enabled])
}

type HotkeyOptions = {
  /** Require Cmd on macOS / Ctrl elsewhere. */
  mod?: boolean
  shift?: boolean
  /** Fire even while a text field has focus. Off by default. */
  allowInInput?: boolean
}

/**
 * Bind a single-key shortcut, e.g. useHotkey('k', openPalette, { mod: true }).
 * Keys are matched case-insensitively against event.key.
 */
export function useHotkey(
  key: string,
  handler: (event: KeyboardEvent) => void,
  { mod = false, shift = false, allowInInput = false }: HotkeyOptions = {}
) {
  const saved = useRef(handler)
  saved.current = handler

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== key.toLowerCase()) return
      if (mod && !(event.metaKey || event.ctrlKey)) return
      if (!mod && (event.metaKey || event.ctrlKey)) return
      if (shift !== event.shiftKey) return

      if (!allowInInput) {
        const target = event.target as HTMLElement | null
        const tag = target?.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return
      }

      event.preventDefault()
      saved.current(event)
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [key, mod, shift, allowInInput])
}

/** Client-side pagination over an array. */
export function usePagination<T>(items: T[], perPage = 25) {
  const [page, setPage] = useState(1)

  const pageCount = Math.max(1, Math.ceil(items.length / perPage))
  const safePage = Math.min(page, pageCount)

  // Snap back to a valid page when the underlying list shrinks.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount)
  }, [page, pageCount])

  const pageItems = useMemo(
    () => items.slice((safePage - 1) * perPage, safePage * perPage),
    [items, safePage, perPage]
  )

  return {
    page: safePage,
    pageCount,
    pageItems,
    setPage,
    next: () => setPage((p) => Math.min(p + 1, pageCount)),
    prev: () => setPage((p) => Math.max(p - 1, 1)),
    from: items.length === 0 ? 0 : (safePage - 1) * perPage + 1,
    to: Math.min(safePage * perPage, items.length),
    total: items.length,
  }
}

export type SortDirection = 'asc' | 'desc'

/** Sort state for a table, cycling asc → desc → none on repeated clicks. */
export function useTableSort<T>(items: T[], initialKey: keyof T | null = null) {
  const [key, setKey] = useState<keyof T | null>(initialKey)
  const [direction, setDirection] = useState<SortDirection>('asc')

  const sorted = useMemo(() => {
    if (key === null) return items
    const factor = direction === 'asc' ? 1 : -1
    return [...items].sort((a, b) => {
      const av = a[key]
      const bv = b[key]
      if (av === bv) return 0
      // Nulls and undefined always sort last, regardless of direction.
      if (av === null || av === undefined) return 1
      if (bv === null || bv === undefined) return -1
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor
      return String(av).localeCompare(String(bv)) * factor
    })
  }, [items, key, direction])

  const toggle = useCallback(
    (next: keyof T) => {
      if (next === key) {
        // asc → desc → off
        if (direction === 'asc') setDirection('desc')
        else {
          setKey(null)
          setDirection('asc')
        }
      } else {
        setKey(next)
        setDirection('asc')
      }
    },
    [key, direction]
  )

  return { sorted, sortKey: key, direction, toggle }
}

/** Detect when an element scrolls into view — used for lazy sections. */
export function useIntersection<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  options: IntersectionObserverInit = { rootMargin: '200px' }
): boolean {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        setVisible(true)
        // One-shot: stop observing once it has been seen.
        observer.disconnect()
      }
    }, options)
    observer.observe(el)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref])

  return visible
}

/** Lock body scroll while a modal or drawer is open. */
export function useLockBodyScroll(locked: boolean) {
  useEffect(() => {
    if (!locked) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [locked])
}
